import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import useStateRef from 'react-usestateref';
import { getCache, setCache } from '../utils/manager/cache-manager';

const StorageContext = createContext(null);

function StorageProvider({ children }) {
  const [menuId, _setMenuId, menuIdRef] = useStateRef(null);
  const [menuSlug, setMenuSlug, menuSlugRef] = useStateRef(null);
  const [menuLayout, _setMenuLayout, menuLayoutRef] = useStateRef(null);
  const [menuInfo, _setMenuInfo, menuInfoRef] = useStateRef(null);
  const [menuCategories, _setMenuCategories, menuCategoriesRef] =
    useStateRef(null);
  const [spot, _setSpot, spotRef] = useStateRef(null);
  const [orders, _setOrders, ordersRef] = useStateRef(null);
  const [languages, _setLanguages, languagesRef] = useStateRef(null);
  const [language, _setLanguage, languageRef] = useStateRef(null);
  const [payment, _setPayment, paymentRef] = useStateRef(null);
  const [paying, setPaying, payingRef] = useStateRef(false);
  const [user, _setUser, userRef] = useStateRef(null);
  const [session, _setSession, sessionRef] = useStateRef(null);
  // const [address, _setAddress, addressRef] = useStateRef(null);
  const [mobile, _setMobile, mobileRef] = useStateRef(null);
  const [config, _setConfig, configRef] = useStateRef(null);
  const [experiment, _setExperiment] = useState(null);
  const [origin, setOrigin] = useState(null);
  const [showingModal, setShowingModal, showingModalRef] = useStateRef(false);
  const [loadedSpot, setLoadedSpot] = useState(null);
  const [loadedCategory, setLoadedCategory] = useState(null);
  const [loadedItem, setLoadedItem] = useState(null);
  const [liveEditMode, setLiveEditMode] = useState();
  const [filter, setFilter] = useState();
  const [filterLoading, setFilterLoading, filterLoadingRef] =
    useStateRef(false);
  const [filterCount, setFilterCount] = useState();
  // const userSetAtRef = useRef();

  useEffect(() => {
    // loadStorage();

    _setLanguages(getCache(null, 'languages'));
    _setConfig(getCache(null, 'config') ?? {});
    _setExperiment(getCache(null, 'experiment'));

    _setUser(getCache(null, 'user'));
  }, []);

  useEffect(() => {
    _setMenuId(getCache(menuSlug, 'menu_id'));
    _setMenuLayout(getCache(menuSlug, 'menu_layout'));
    _setMenuInfo(getCache(menuSlug, 'menu_info'));
    _setMenuCategories(getCache(menuSlug, 'menu_categories'));

    _setSession(getCache(menuSlug, 'session'));
    _setPayment(getCache(menuSlug, 'payment'));
    _setOrders(getCache(menuSlug, 'orders'));
    _setLanguage(getCache(menuSlug, 'language'));

    _setSpot(getCache(menuSlug, 'spot'));
  }, [menuSlug]);

  useEffect(() => {
    // _setAddress(getCache(user?.id, 'address'));
    _setMobile(getCache(user?.id, 'mobile'));
  }, [user]);

  // function loadStorage() {
  //   const slug = menuSlugRef.current;

  //   _setMenuId(getCache(slug, 'menu_id'));
  //   _setMenuLayout(getCache(slug, 'menu_layout'));
  //   _setMenuInfo(getCache(slug, 'menu_info'));
  //   _setMenuCategories(getCache(slug, 'menu_categories'));

  //   _setSession(getCache(slug, 'session'));
  //   _setPayment(getCache(slug, 'payment'));
  //   _setOrders(getCache(slug, 'orders'));
  //   _setLanguages(getCache(null, 'languages'));
  //   _setLanguage(getCache(slug, 'language'));
  //   _setConfig(getCache(null, 'config') ?? {});
  //   _setExperiment(getCache(null, 'experiment'));

  //   _setUser(getCache(null, 'user'));
  //   _setSpot(getCache(slug, 'spot'));
  // }

  const setMenuId = useCallback(
    (pMenuId) => {
      setCache(menuSlug, 'menu_id', pMenuId);
      _setMenuId(pMenuId);
    },
    [menuSlug]
  );

  const setMenuLayout = useCallback(
    (pMenuLayout) => {
      setCache(menuSlug, 'menu_layout', pMenuLayout);
      _setMenuLayout(pMenuLayout);
    },
    [menuSlug]
  );

  const setMenuInfo = useCallback(
    (pMenuInfo) => {
      setCache(menuSlug, 'menu_info', pMenuInfo);
      _setMenuInfo(pMenuInfo);
    },
    [menuSlug]
  );

  const setMenuCategories = useCallback(
    (pMenuCategories) => {
      setCache(menuSlug, 'menu_categories', pMenuCategories);
      _setMenuCategories(pMenuCategories);
    },
    [menuSlug]
  );

  const setPayment = useCallback(
    (_payment) => {
      setCache(menuSlug, 'payment', _payment);
      _setPayment(_payment);
    },
    [menuSlug]
  );

  const setSession = useCallback(
    (_session) => {
      // setCache(menuSlug, 'session', _session, {
      //   shelfLife: { value: 4, unit: 'h' },
      // });
      setCache(menuSlug, 'session', _session);
      _setSession(_session);
    },
    [menuSlug]
  );

  // const setAddress = useCallback(
  //   (_address) => {
  //     setCache(user?.id, 'address', _address, {
  //       shelfLife: { value: 4, unit: 'h' },
  //     });
  //     _setAddress(_address);
  //   },
  //   [user]
  // );

  const setMobile = useCallback(
    (_mobile) => {
      setCache(user?.id, 'mobile', _mobile, {
        shelfLife: { value: 4, unit: 'h' },
      });
      _setMobile(_mobile);
    },
    [user]
  );

  const setConfig = useCallback((_config) => {
    setCache(null, 'config', _config);
    _setConfig(_config ?? {});
  }, []);

  const setSpot = useCallback(
    (_spot) => {
      let props = { shelfLife: { value: 4, unit: 'h' } };

      if (_spot?.type?.addressable) {
        props = null;
      }

      setCache(menuSlug, 'spot', _spot, props);
      _setSpot(_spot);
    },
    [menuSlug]
  );

  const setOrders = useCallback(
    (_orders) => {
      setCache(menuSlug, 'orders', _orders, {
        shelfLife: { value: 4, unit: 'h' },
      });
      _setOrders(_orders);
    },
    [menuSlug]
  );

  const setLanguages = useCallback((_languages) => {
    setCache(null, 'languages', _languages);
    _setLanguages(_languages);
  }, []);

  const setLanguage = useCallback(
    (_language) => {
      setCache(menuSlug, 'language', _language);
      _setLanguage(_language);
      // TODO: Não pode fazer isso aqui. Isso é uma marreta!
      // window.location.reload();
    },
    [menuSlug, languagesRef.current]
  );

  const setExperiment = useCallback((_experiment) => {
    setCache(null, 'experiment', _experiment, {
      shelfLife: { value: 7, unit: 'd' },
    });
    _setExperiment(_experiment);
  }, []);

  const setUser = useCallback((_user) => {
    setCache(null, 'user', _user);
    _setUser(_user);
    // userSetAtRef.current = dayjs();
  }, []);

  const value = useMemo(
    () => ({
      // loadStorage,

      menuId,
      setMenuId,
      menuIdRef,
      menuSlug,
      setMenuSlug,
      menuSlugRef,
      menuLayout,
      setMenuLayout,
      menuLayoutRef,
      menuInfo,
      setMenuInfo,
      menuInfoRef,
      menuCategories,
      setMenuCategories,
      menuCategoriesRef,

      spot,
      spotRef,
      setSpot,

      loadedSpot,
      setLoadedSpot,
      loadedCategory,
      setLoadedCategory,
      loadedItem,
      setLoadedItem,
      liveEditMode,
      setLiveEditMode,
      filter,
      setFilter,
      filterLoading,
      setFilterLoading,
      filterLoadingRef,
      filterCount,
      setFilterCount,

      orders,
      setOrders,
      ordersRef,
      languages,
      setLanguages,
      languagesRef,

      language,
      setLanguage,
      languageRef,

      session,
      setSession,
      sessionRef,

      // address,
      // setAddress,
      // addressRef,
      mobile,
      setMobile,
      mobileRef,

      payment,
      setPayment,
      paymentRef,

      paying,
      setPaying,
      payingRef,

      config,
      setConfig,
      configRef,
      user,
      userRef,
      setUser,
      // userSetAtRef,
      experiment,
      setExperiment,
      origin,
      setOrigin,
      showingModal,
      setShowingModal,
      showingModalRef,
    }),
    [
      // loadStorage,

      menuId,
      setMenuId,
      menuIdRef,
      menuSlug,
      setMenuSlug,
      menuSlugRef,
      menuLayout,
      setMenuLayout,
      menuLayoutRef,
      menuInfo,
      setMenuInfo,
      menuInfoRef,
      menuCategories,
      setMenuCategories,
      menuCategoriesRef,

      spot,
      spotRef,
      setSpot,

      loadedSpot,
      setLoadedSpot,
      loadedCategory,
      setLoadedCategory,
      loadedItem,
      setLoadedItem,
      liveEditMode,
      setLiveEditMode,
      filter,
      setFilter,
      filterLoading,
      setFilterLoading,
      filterLoadingRef,
      filterCount,
      setFilterCount,

      orders,
      setOrders,
      ordersRef,
      session,
      setSession,
      sessionRef,

      // address,
      // setAddress,
      // addressRef,
      mobile,
      setMobile,
      mobileRef,

      payment,
      setPayment,
      paymentRef,

      paying,
      setPaying,
      payingRef,

      config,
      setConfig,
      configRef,

      user,
      userRef,
      setUser,
      // userSetAtRef,
      experiment,
      setExperiment,
      origin,
      setOrigin,
      showingModal,
      setShowingModal,
      showingModalRef,
    ]
  );

  return (
    <StorageContext.Provider value={value}>{children}</StorageContext.Provider>
  );
}

function useStorage(bypassContextError = false) {
  const context = useContext(StorageContext);

  if (!bypassContextError && !context) {
    throw new Error('useStorage must be used within an StorageProvider.');
  }

  return context;
}

export { StorageProvider, useStorage };
